import {
  AnimatePresence,
  Badge,
  Button,
  Flex,
  Motion,
  Text,
} from "../../components";
import {
  ArrowUpOutlined,
  SearchOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "../../hooks";
import { useLocation } from "react-router-dom";
import { useAppStore, useCartStore } from "../../stores";
import { OrderType } from "../../types/enums";
import { Tools } from "../../utils";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const JumpButtons = ({ cartVisible }: { cartVisible: boolean }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const field = document.getElementById("menu-search");
      if (!field) {
        setScrolled(false);
        return;
      }
      const top = field.getBoundingClientRect().top + window.scrollY;
      const next = window.scrollY > top;
      setScrolled((current) => (current === next ? current : next));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollToSearch = () => {
    const field = document.getElementById("menu-search");
    if (!field) return;
    field.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => {
      field.querySelector("input")?.focus({ preventScroll: true });
    }, 400);
  };

  const actions = [
    {
      key: "menu-back-to-top",
      label: "Back to top",
      icon: <ArrowUpOutlined />,
      place: "right-[2.4rem] mobile:right-[1.6rem]",
      onClick: () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
    },
    {
      key: "menu-search-jump",
      label: "Search menu",
      icon: <SearchOutlined />,
      place: "left-[2.4rem] mobile:left-[1.6rem]",
      onClick: scrollToSearch,
    },
  ];

  return (
    <AnimatePresence>
      {scrolled &&
        actions.map((action) => (
          <Motion
            as="div"
            name="scale"
            key={action.key}
            style={{ transformOrigin: "center center" }}
            className={twMerge(
              "fixed z-[210] bottom-[2.4rem]",
              action.place,
              cartVisible ? "mobile:bottom-[8rem]" : "mobile:bottom-[2.4rem]",
            )}
          >
            <Button
              shape="round"
              icon={action.icon}
              aria-label={action.label}
              onClick={action.onClick}
              className={"!h-[4.4rem] !w-[4.4rem] text-[18px] !shadow-xl"}
            />
          </Motion>
        ))}
    </AnimatePresence>
  );
};

const CartFloatingBar = () => {
  const { pathname } = useLocation();
  const { searchParams, setSearchParams } = useSearchParams({ cart: false });
  const items = useCartStore((state) => state.items);
  const storedCount = useCartStore((state) => state.totalItemsInCart);
  const grandtotal = useCartStore((state) => state.grandtotal);
  const subtotal = useCartStore((state) => state.subtotal);
  const orderType = useAppStore((state) => state.location?.orderType);
  const totalItemsInCart =
    storedCount || items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const visible =
    totalItemsInCart > 0 && pathname !== "/checkout" && !searchParams.cart;
  const onViewCart = () => setSearchParams({ cart: true });
  const lineTotal = items.reduce(
    (sum, item) =>
      sum +
      Number(
        item.lineTotal ??
          item.totalPrice ??
          (item.price || 0) * (item.quantity || 0),
      ),
    0,
  );
  const total = grandtotal || subtotal || lineTotal;
  const itemLabel = `${totalItemsInCart} ${
    totalItemsInCart === 1 ? "item" : "items"
  }`;
  const subtitle =
    orderType === OrderType.pickup
      ? `${itemLabel} • Pickup`
      : `${itemLabel} in your cart`;

  const prevCount = useRef(0);
  const bounceKey = useRef(0);

  if (visible && prevCount.current && totalItemsInCart > prevCount.current) {
    bounceKey.current += 1;
  }
  prevCount.current = visible ? totalItemsInCart : 0;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <Motion
            as="div"
            name="rise"
            key="cart-floating-bar"
            id="cart-floating-bar"
            className="fixed inset-x-0 bottom-[2.4rem] z-[200] mx-auto w-[min(56rem,calc(100%-4.8rem))] mobile:bottom-0 mobile:w-full"
          >
            <Motion
              as="div"
              key={bounceKey.current}
              name={bounceKey.current ? "nudge" : undefined}
              onClick={onViewCart}
              className="flex cursor-pointer items-center gap-[2.2rem] rounded-card bg-surface-header px-[1.8rem] py-[1.2rem] shadow-[0_8px_24px_rgba(0,0,0,0.14)] mobile:rounded-none mobile:px-[1.6rem] mobile:pb-[max(1.2rem,env(safe-area-inset-bottom))] mobile:shadow-sticky"
            >
              <Badge count={totalItemsInCart}>
                <ShoppingOutlined className="text-[2.4rem]" />
              </Badge>
              <Flex vertical flex={1} className="min-w-0">
                <Text weight={700} children={Tools.toCurrency(total)} />
                <Text
                  type="secondary"
                  size="1.3rem"
                  lineHeight="1.6rem"
                  children={subtitle}
                />
              </Flex>
              <Button
                color="base"
                title="View cart"
                className="!min-w-[12rem]"
                onClick={onViewCart}
              />
            </Motion>
          </Motion>
        )}
      </AnimatePresence>
      {pathname === "/" && <JumpButtons cartVisible={visible} />}
    </>
  );
};

export default CartFloatingBar;
